import React, { useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import {
  formatDbStatus,
  getStatusHighlight,
  OperatorStatusData,
  OperatorStatusRow,
  parseOperatorResponseRows,
  StatusHighlight,
} from "../../../../utils/operatorStatusDisplay";

interface OperatorStatusResultProps {
  data: OperatorStatusData;
  sideLabel: string;
}

const highlightClasses: Record<StatusHighlight, string> = {
  success: "bg-emerald-50 text-emerald-800 border-emerald-200",
  error: "bg-red-50 text-red-800 border-red-200",
  warning: "bg-amber-50 text-amber-800 border-amber-200",
  neutral: "bg-gray-50 text-gray-800 border-gray-200",
};

const StatusBadge: React.FC<{ value?: string; highlight?: StatusHighlight }> = ({ value, highlight }) => {
  if (!value) return <span className="text-gray-400">—</span>;
  const tone = highlight ?? getStatusHighlight(value);
  return (
    <span className={`inline-flex px-2.5 py-1 text-xs font-bold rounded-lg border ${highlightClasses[tone]}`}>
      {formatDbStatus(value)}
    </span>
  );
};

const SummaryCard: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="p-3 bg-white rounded-xl border border-gray-200">
    <p className="mb-1 text-xs font-bold tracking-wide text-gray-500 uppercase">{label}</p>
    <div className="text-sm font-semibold text-gray-900">{children}</div>
  </div>
);

const OperatorDetailsTable: React.FC<{ rows: OperatorStatusRow[] }> = ({ rows }) => {
  if (rows.length === 0) {
    return (
      <p className="p-4 text-sm text-gray-500 bg-gray-50 rounded-xl border border-gray-200">
        Aucun détail opérateur disponible.
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-100 border-b border-gray-200">
            <th className="px-4 py-3 w-2/5 text-xs font-black tracking-wide text-left text-gray-600 uppercase">
              Information
            </th>
            <th className="px-4 py-3 text-xs font-black tracking-wide text-left text-gray-600 uppercase">Valeur</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`${row.label}-${index}`} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
              <td className="px-4 py-3 align-top font-semibold text-gray-600">{row.label}</td>
              <td className="px-4 py-3 align-top">
                {row.highlight ? (
                  <span
                    className={`inline-flex px-2.5 py-1 text-xs font-bold rounded-lg border ${highlightClasses[row.highlight]}`}
                  >
                    {row.value}
                  </span>
                ) : (
                  <span className="font-medium text-gray-900 break-words">{row.value}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const OperatorStatusResult: React.FC<OperatorStatusResultProps> = ({ data, sideLabel }) => {
  const [showRawJson, setShowRawJson] = useState(false);

  const operatorRows = useMemo(
    () => parseOperatorResponseRows(data.operator_response, data.provider),
    [data.operator_response, data.provider]
  );

  const isCanadaSide = data.side === "canada" || sideLabel.toLowerCase() === "canada";

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-black tracking-wide text-gray-500 uppercase">{sideLabel}</p>
        {data.country && <p className="mt-1 text-lg font-bold text-gray-900">{data.country}</p>}
      </div>

      {data.read_only && (
        <div className="flex gap-3 items-start p-4 bg-blue-50 rounded-xl border border-blue-200">
          <AlertTriangle className="flex-shrink-0 mt-0.5 w-5 h-5 text-blue-600" />
          <p className="text-sm font-medium text-blue-900">
            {data.message || "Consultation opérateur uniquement — aucune modification en base."}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <SummaryCard label="Statut en base (paiement)">
          <StatusBadge value={data.payment_status} highlight={getStatusHighlight(data.payment_status)} />
        </SummaryCard>
        <SummaryCard label="Statut en base (payout)">
          <StatusBadge value={data.payout_status} highlight={getStatusHighlight(data.payout_status)} />
        </SummaryCard>
        {!isCanadaSide && (
          <>
            <SummaryCard label="Statut opérateur">
              <StatusBadge
                value={data.operator_status}
                highlight={getStatusHighlight(data.operator_status_normalized || data.operator_status)}
              />
            </SummaryCard>
            <SummaryCard label="Opérateur / réseau">
              <span className="text-sm font-semibold text-gray-900">{data.provider || "—"}</span>
            </SummaryCard>
          </>
        )}
        {isCanadaSide && data.provider && (
          <SummaryCard label="Fournisseur Canada">
            <span className="text-sm font-semibold text-gray-900">{data.provider}</span>
          </SummaryCard>
        )}
      </div>

      {!isCanadaSide && data.operator_response != null && (
        <div className="space-y-3">
          <h3 className="text-sm font-black tracking-wide text-gray-700 uppercase">Détails opérateur</h3>
          <OperatorDetailsTable rows={operatorRows} />
        </div>
      )}

      {!isCanadaSide && data.operator_response != null && (
        <div className="pt-2 border-t border-gray-200">
          <button
            type="button"
            onClick={() => setShowRawJson((prev) => !prev)}
            className="flex gap-2 items-center text-xs font-bold text-gray-500 transition-colors hover:text-gray-700"
          >
            {showRawJson ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {showRawJson ? "Masquer la réponse brute" : "Voir la réponse brute (JSON)"}
          </button>
          {showRawJson && (
            <pre className="overflow-x-auto p-4 mt-3 max-h-64 text-xs bg-gray-900 rounded-xl text-gray-100">
              {JSON.stringify(data.operator_response, null, 2)}
            </pre>
          )}
        </div>
      )}

      {data.payment_id && (
        <p className="text-xs text-gray-400">
          Référence transaction : <span className="font-mono">{data.payment_id}</span>
        </p>
      )}
    </div>
  );
};

export default OperatorStatusResult;
